/**
 * Progress Tracking Service (Simplified)
 *
 * Clean, simple service for tracking user progress through lessons.
 * Uses three tables:
 *   - lesson_progress: Track lesson start/completion with time
 *   - page_views: Track user journey through pages
 *   - user_events: Track interactions and clicks
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// TYPES
// ============================================================================

export type LessonStatus = 'started' | 'in_progress' | 'completed';

export interface LessonProgress {
  id: string;
  userId: string;
  lessonId: string;
  status: LessonStatus;
  progressPercent: number;
  startedAt: Date;
  completedAt?: Date;
  timeSpentSeconds: number;
  progressData: Record<string, any>;
}

export interface PageView {
  id: string;
  userId: string;
  pagePath: string;
  pageTitle?: string;
  sessionId?: string;
  referrer?: string;
  timeOnPageSeconds?: number;
  createdAt: Date;
}

export interface UserEvent {
  id: string;
  userId: string;
  eventType: string;
  eventName: string;
  eventData: Record<string, any>;
  pagePath?: string;
  createdAt: Date;
}

export interface ProgressUpdateRequest {
  lessonId: string;
  status?: LessonStatus;
  progressPercent?: number;
  timeSpentSeconds?: number;
  progressData?: Record<string, any>;
}

export interface ProgressUpdateResponse {
  success: boolean;
  progress: LessonProgress | null;
  error?: string;
}

// Legacy type aliases for backward compatibility
export type ContentType = 'lesson' | 'workshop' | 'simulator' | 'practice_session' | 'assessment' | 'chapter';
export type ProgressStatus = LessonStatus | 'not_started' | 'paused' | 'skipped';

// Legacy interface for backward compatibility
export interface UserProgressDetailed {
  id: string;
  userId: string;
  contentType: string;
  contentId: string;
  progressData: Record<string, any>;
  percentageComplete: number;
  timeSpentSeconds: number;
  status: string;
  milestonesCompleted: string[];
  currentMilestone?: string;
  firstAccessedAt?: Date;
  lastAccessedAt: Date;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// SERVICE
// ============================================================================

export class ProgressTrackingService {
  private userId: string | null = null;
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeUser();

    // Keep userId in sync with auth state
    supabase.auth.onAuthStateChange((_event, session) => {
      this.userId = session?.user?.id || null;
    });
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async initializeUser(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    this.userId = user?.id || null;
  }

  private async ensureUser(): Promise<string> {
    if (!this.userId) {
      const { data: { user } } = await supabase.auth.getUser();
      this.userId = user?.id || null;
    }
    if (!this.userId) {
      throw new Error('User not authenticated');
    }
    return this.userId;
  }

  // ==========================================================================
  // LESSON PROGRESS
  // ==========================================================================

  /**
   * Start tracking a lesson (call when user enters lesson)
   */
  async startLesson(lessonId: string): Promise<ProgressUpdateResponse> {
    try {
      const userId = await this.ensureUser();

      const { data, error } = await supabase
        .from('lesson_progress')
        .upsert({
          user_id: userId,
          lesson_id: lessonId,
          status: 'started',
          progress_percent: 0,
          started_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,lesson_id'
        })
        .select()
        .single();

      if (error) throw error;

      // Also log a page view
      await this.trackPageView(`/lesson/${lessonId}`);

      // Log event
      await this.trackEvent('lesson', 'lesson_started', { lessonId });

      return {
        success: true,
        progress: this.mapLessonProgress(data)
      };
    } catch (error: any) {
      console.error('[ProgressTracking] Failed to start lesson:', error);
      return { success: false, progress: null, error: error.message };
    }
  }

  /**
   * Update lesson progress (call during lesson)
   */
  async updateProgress(request: ProgressUpdateRequest): Promise<ProgressUpdateResponse> {
    try {
      const userId = await this.ensureUser();

      // Get existing progress to accumulate time
      const { data: existing } = await supabase
        .from('lesson_progress')
        .select('time_spent_seconds, status, progress_percent')
        .eq('user_id', userId)
        .eq('lesson_id', request.lessonId)
        .single();

      const currentTime = existing?.time_spent_seconds || 0;
      const newTime = request.timeSpentSeconds || 0;

      // Preserve existing values if not provided
      const status = request.status || existing?.status || 'in_progress';
      const progressPercent = request.progressPercent ?? existing?.progress_percent ?? 0;

      const updateData: any = {
        user_id: userId,
        lesson_id: request.lessonId,
        status,
        progress_percent: progressPercent,
        time_spent_seconds: currentTime + newTime,
        progress_data: request.progressData || {}
      };

      // Set completed_at if completing
      if (status === 'completed') {
        updateData.completed_at = new Date().toISOString();
        updateData.progress_percent = 100;
      }

      const { data, error } = await supabase
        .from('lesson_progress')
        .upsert(updateData, { onConflict: 'user_id,lesson_id' })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        progress: this.mapLessonProgress(data)
      };
    } catch (error: any) {
      console.error('[ProgressTracking] Failed to update progress:', error);
      return { success: false, progress: null, error: error.message };
    }
  }

  /**
   * Complete a lesson (call when user finishes)
   * @param lessonId - The lesson ID
   * @param timeSpentSeconds - Optional: elapsed time in seconds for this session (added to existing time)
   * @param progressData - Optional: additional progress data to store
   */
  async completeLesson(
    lessonId: string,
    timeSpentSeconds?: number,
    progressData?: Record<string, any>
  ): Promise<ProgressUpdateResponse> {
    try {
      const userId = await this.ensureUser();

      // Get existing to preserve accumulated time
      const { data: existing } = await supabase
        .from('lesson_progress')
        .select('time_spent_seconds, progress_data')
        .eq('user_id', userId)
        .eq('lesson_id', lessonId)
        .single();

      const mergedData = {
        ...(existing?.progress_data || {}),
        ...(progressData || {})
      };

      // Add new session time to existing accumulated time
      const existingTime = existing?.time_spent_seconds || 0;
      const sessionTime = timeSpentSeconds || 0;
      const totalTime = existingTime + sessionTime;

      const { data, error } = await supabase
        .from('lesson_progress')
        .upsert({
          user_id: userId,
          lesson_id: lessonId,
          status: 'completed',
          progress_percent: 100,
          completed_at: new Date().toISOString(),
          time_spent_seconds: totalTime,
          progress_data: mergedData
        }, { onConflict: 'user_id,lesson_id' })
        .select()
        .single();

      if (error) throw error;

      // Log event
      await this.trackEvent('lesson', 'lesson_completed', {
        lessonId,
        sessionTimeSpent: sessionTime,
        totalTimeSpent: totalTime
      });

      return {
        success: true,
        progress: this.mapLessonProgress(data)
      };
    } catch (error: any) {
      console.error('[ProgressTracking] Failed to complete lesson:', error);
      return { success: false, progress: null, error: error.message };
    }
  }

  /**
   * Get progress for a specific lesson
   */
  async getProgress(lessonId: string): Promise<LessonProgress | null> {
    try {
      const userId = await this.ensureUser();

      const { data, error } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('lesson_id', lessonId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data ? this.mapLessonProgress(data) : null;
    } catch (error) {
      console.error('[ProgressTracking] Failed to get progress:', error);
      return null;
    }
  }

  /**
   * Get all progress for current user
   */
  async getAllProgress(): Promise<LessonProgress[]> {
    try {
      const userId = await this.ensureUser();

      const { data, error } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(this.mapLessonProgress);
    } catch (error) {
      console.error('[ProgressTracking] Failed to get all progress:', error);
      return [];
    }
  }

  /**
   * Check if a lesson is completed
   */
  async isLessonCompleted(lessonId: string): Promise<boolean> {
    const progress = await this.getProgress(lessonId);
    return progress?.status === 'completed';
  }

  // ==========================================================================
  // PAGE VIEWS (User Journey)
  // ==========================================================================

  /**
   * Track a page view
   */
  async trackPageView(
    pagePath: string,
    pageTitle?: string,
    referrer?: string
  ): Promise<void> {
    try {
      const userId = await this.ensureUser();

      await supabase.from('page_views').insert({
        user_id: userId,
        page_path: pagePath,
        page_title: pageTitle,
        session_id: this.sessionId,
        referrer: referrer || document.referrer || null
      });
    } catch (error) {
      // Silent fail - don't block user experience for analytics
      console.warn('[ProgressTracking] Failed to track page view:', error);
    }
  }

  /**
   * Update time spent on a page (call before leaving)
   */
  async updatePageTime(pagePath: string, timeSeconds: number): Promise<void> {
    try {
      const userId = await this.ensureUser();

      // Update the most recent page view for this path in this session
      await supabase
        .from('page_views')
        .update({ time_on_page_seconds: timeSeconds })
        .eq('user_id', userId)
        .eq('page_path', pagePath)
        .eq('session_id', this.sessionId)
        .order('created_at', { ascending: false })
        .limit(1);
    } catch (error) {
      console.warn('[ProgressTracking] Failed to update page time:', error);
    }
  }

  // ==========================================================================
  // USER EVENTS (Interactions)
  // ==========================================================================

  /**
   * Track a user event/interaction
   */
  async trackEvent(
    eventType: string,
    eventName: string,
    eventData?: Record<string, any>,
    pagePath?: string
  ): Promise<void> {
    try {
      const userId = await this.ensureUser();

      await supabase.from('user_events').insert({
        user_id: userId,
        event_type: eventType,
        event_name: eventName,
        event_data: eventData || {},
        page_path: pagePath || window.location.pathname
      });
    } catch (error) {
      // Silent fail
      console.warn('[ProgressTracking] Failed to track event:', error);
    }
  }

  // ==========================================================================
  // LEGACY COMPATIBILITY METHODS
  // ==========================================================================

  /**
   * Legacy method - maps to new system
   * @deprecated Use updateProgress directly
   */
  async updateLegacyProgress(request: {
    contentType?: string;
    contentId: string;
    status?: string;
    percentageComplete?: number;
    timeSpentSeconds?: number;
    progressData?: Record<string, any>;
  }): Promise<any> {
    // Map legacy request to new format
    const lessonId = request.contentId;
    let status: LessonStatus = 'in_progress';

    if (request.status === 'completed') status = 'completed';
    else if (request.status === 'not_started') status = 'started';
    else if (request.status === 'in_progress') status = 'in_progress';

    const result = await this.updateProgress({
      lessonId,
      status,
      progressPercent: request.percentageComplete,
      timeSpentSeconds: request.timeSpentSeconds,
      progressData: request.progressData
    });

    // Return legacy format for backward compatibility
    return {
      userProgress: result.progress ? this.mapToLegacyFormat(result.progress, request.contentType || 'lesson') : null,
      badgesAwarded: [],
      milestonesCompleted: [],
      analyticsUpdated: result.success,
      error: result.error
    };
  }

  /**
   * Legacy method - get progress in old format
   * @deprecated Use getProgress directly
   */
  async getLegacyProgress(contentType?: string, contentId?: string): Promise<UserProgressDetailed[]> {
    if (contentId) {
      const progress = await this.getProgress(contentId);
      if (!progress) return [];
      return [this.mapToLegacyFormat(progress, contentType || 'lesson')];
    }

    const allProgress = await this.getAllProgress();
    return allProgress.map(p => this.mapToLegacyFormat(p, contentType || 'lesson'));
  }

  /**
   * Legacy dashboard summary - computed from actual data
   */
  async getDashboardSummary(): Promise<{
    totalLessonsCompleted: number;
    totalTimeSpent: number;
    currentStreak: number;
  }> {
    try {
      const allProgress = await this.getAllProgress();

      const completed = allProgress.filter(p => p.status === 'completed').length;
      const totalTime = allProgress.reduce((sum, p) => sum + p.timeSpentSeconds, 0);

      return {
        totalLessonsCompleted: completed,
        totalTimeSpent: totalTime,
        currentStreak: 0 // Would need separate calculation
      };
    } catch {
      return { totalLessonsCompleted: 0, totalTimeSpent: 0, currentStreak: 0 };
    }
  }

  /**
   * Track profile update - legacy compatibility
   */
  async trackProfileUpdate(field: string, oldValue: any, newValue: any): Promise<void> {
    await this.trackEvent('profile', 'profile_updated', {
      field,
      oldValue,
      newValue
    });
  }

  /**
   * Get incomplete content for session recovery
   */
  async getIncompleteContent(): Promise<LessonProgress[]> {
    try {
      const userId = await this.ensureUser();

      const { data, error } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('user_id', userId)
        .in('status', ['started', 'in_progress'])
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(this.mapLessonProgress);
    } catch (error) {
      console.error('[ProgressTracking] Failed to get incomplete content:', error);
      return [];
    }
  }

  /**
   * Subscribe to progress changes - legacy compatibility (no-op)
   */
  subscribeToProgress(_callback: (progress: any) => void): () => void {
    // Real-time subscriptions could be added if needed
    return () => {};
  }

  /**
   * Get leaderboard - legacy stub
   */
  async getLeaderboard(_request?: any): Promise<{ entries: any[] }> {
    return { entries: [] };
  }

  /**
   * Get badges - legacy stub
   */
  async getAvailableBadges(): Promise<any[]> {
    return [];
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  private mapLessonProgress(data: any): LessonProgress {
    return {
      id: data.id,
      userId: data.user_id,
      lessonId: data.lesson_id,
      status: data.status,
      progressPercent: data.progress_percent || 0,
      startedAt: new Date(data.started_at || data.created_at),
      completedAt: data.completed_at ? new Date(data.completed_at) : undefined,
      timeSpentSeconds: data.time_spent_seconds || 0,
      progressData: data.progress_data || {}
    };
  }

  private mapToLegacyFormat(progress: LessonProgress, contentType: string): UserProgressDetailed {
    return {
      id: progress.id,
      userId: progress.userId,
      contentType,
      contentId: progress.lessonId,
      progressData: progress.progressData,
      percentageComplete: progress.progressPercent,
      timeSpentSeconds: progress.timeSpentSeconds,
      status: progress.status,
      milestonesCompleted: [],
      currentMilestone: undefined,
      firstAccessedAt: progress.startedAt,
      lastAccessedAt: progress.startedAt,
      completedAt: progress.completedAt,
      createdAt: progress.startedAt,
      updatedAt: progress.startedAt
    };
  }

  /**
   * Cleanup - called on logout
   */
  cleanup(): void {
    this.userId = null;
  }
}

// Export singleton instance
export const progressTrackingService = new ProgressTrackingService();

// Default export for backward compatibility
export default progressTrackingService;
